import React, { useEffect } from 'react';
import cx from 'classnames';
import { useRouter } from 'next/router';

import { SLUG_BRAZE_DICT } from 'Components/Account/AccountPage.consts';
import {
	isAccountCategory,
	isAccountSlug,
	isPurchasesCategory,
	parseQueryObject,
} from 'Components/Account/AccountPage.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { SellerCommunicationsModal } from 'Features/SellerCommunications';
import { useBraze } from 'Services/braze';
import { LIST_POSITION_PAGES } from 'Stores/VehicleListPositionStore/VehicleListPositionsStore.consts';
import { AccountCategory, AccountSlugs } from 'Types/account';
import { applyCypressData, scrollIntoView } from 'Utilities/helpers';
import { useAccountVehicleList } from 'Utilities/hooks/account/useAccountVehicleList';
import useVehicleListPosition from 'Utilities/hooks/vehicleList/useVehicleListPosition/useVehicleListPosition';
import ROUTES from 'Utilities/routes';

import { BidsAndOffers } from './BidsAndOffers/BidsAndOffers';
import { Purchases } from './Purchases/Purchases';
import { Sidebar } from './Sidebar/Sidebar';

import styles from './AccountPage.module.scss';

type AccountPageProps = {
	category: AccountCategory;
};

const AccountPage: React.FC<AccountPageProps> = ({ category }) => {
	const { events, push, query } = useRouter();
	const { slug } = useAccountRouter();

	const {
		minWidth: { breakpointDesktopAccount },
	} = useBreakpoint();

	const showSidebar = breakpointDesktopAccount;

	const params = parseQueryObject(query);

	const { data, dataUpdatedAt, isPreviousData, status } = useAccountVehicleList({ keepPreviousData: true, params });

	const PageSection = isPurchasesCategory(category) ? Purchases : BidsAndOffers;

	useVehicleListPosition(false, LIST_POSITION_PAGES.account);

	useEffect(() => {
		const scrollToTarget = document.body;
		scrollIntoView(scrollToTarget, { behavior: 'smooth' });

		const handleRouteChange = (route: string) => {
			if (!route.includes(ROUTES.VEHICLE_LIST.as())) {
				scrollIntoView(scrollToTarget, { behavior: 'smooth' });
			}
		};

		events.on('routeChangeStart', handleRouteChange);

		return () => {
			events.off('routeChangeStart', handleRouteChange);
		};
	}, [events]);

	useBraze({
		eventFn: SLUG_BRAZE_DICT[slug],
	});

	if (!isAccountSlug(slug) || !isAccountCategory(category)) {
		void push(ROUTES.ACCOUNT.as(AccountCategory.BIDS, AccountSlugs.ACTIVE));

		return <></>;
	}

	const shouldAddSellerCommunications = slug === AccountSlugs.COMPLETE;

	return (
		<div className={styles.background}>
			<div
				className={cx(styles.container, {
					[styles.withoutSidebar]: !showSidebar,
				})}
			>
				{showSidebar && <Sidebar />}
				<main {...applyCypressData(cypressIds.containers.account)} className={styles.main}>
					<PageSection data={data} isPreviousData={isPreviousData} queryStatus={status} slug={slug} />
				</main>
			</div>
			{shouldAddSellerCommunications && (
				<SellerCommunicationsModal enquiries={data?.results} updatedAt={dataUpdatedAt} />
			)}
		</div>
	);
};

export { AccountPage };
